import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { normalizePhoneNumber } from '../helper/normalizePhoneNumber';
import { containsProfanity } from './profanityFilter';

// Single-word username: at least 2 unicode letters, optionally followed by
// digits (e.g. "Mohamed", "John99"). No spaces or symbols.
const USERNAME_FORMAT = /^\p{L}{2,}\d*$/u;

export const useSignUpValidation = () => {
    const { t } = useTranslation("signup");
    // signup validation schema
    const signupValidationSchema = yup.object({
        firstName: yup
            .string().trim()
            .required(t("formErrors.firstName.required"))
            .max(50, t("formErrors.firstName.maxLength")),
        lastName: yup
            .string().trim()
            .required(t("formErrors.lastName.required"))
            .max(50, t("formErrors.lastName.maxLength")),
        username: yup
            .string().trim()
            .required(t("formErrors.username.required"))
            .min(3, t("formErrors.username.minLength"))
            .max(30, t("formErrors.username.maxLength"))
            .matches(
                USERNAME_FORMAT,
                t("formErrors.username.lettersOnly")
            )
            .test(
                "no-profanity",
                t("formErrors.username.inappropriate"),
                (value) => !containsProfanity(value)
            ),
        identifier: yup
            .string()
            .required(t("formErrors.identifier.required"))
            .test("email-or-phone", t("formErrors.identifier.invalid"), function (value) {
                const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

                if (emailRegex.test(value)) {
                    return true; // Valid email
                }

                const phoneNumber = normalizePhoneNumber(
                    this.parent.countryCode,
                    value
                );

                return /^\+[1-9]\d{7,14}$/.test(phoneNumber);
            }),
        password: yup
            .string()
            .required(t("formErrors.password.required"))
            .min(8, t("formErrors.password.minLength"))
            .matches(
                /^(?=.*?[A-Za-z])(?=.*?[0-9]).{8,}$/,
                t("formErrors.password.invalidFormat")
            ),
        countryCode: yup.string().when("identifier", {
            is: (identifier) => /^\d+$/.test(identifier),
            then: (schema) =>
                schema.required(t("formErrors.countryCode.required")),
        }),
        terms: yup
            .boolean().oneOf([true], t("formErrors.terms.required")),
    });

    return { signupValidationSchema }
}

